import React, { useState } from 'react';
import {
  Show, TabbedShowLayout, Tab, TextField, DateField, FunctionField, EditButton, TopToolbar, useRecordContext, useDataProvider, useNotify, useRefresh,
} from 'react-admin';
import BolaoGuessesTab from './BolaoGuessesTab';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField as MuiTextField, Typography,
} from '@mui/material';
import { DEFAULT_API_ADMIN } from '../../constants';
import { getChainId, httpClientReactAdmin } from '../../lib';
import BolaoStatusChip from './BolaoStatusChip';

interface PreviewWinner {
  guess_id: string;
  customer_id: string;
  name: string;
  cellphone: string;
  cpf: string;
  score_home: number;
  score_away: number;
  submitted_at: string;
}

const Title = () => {
  const r = useRecordContext();
  if (!r) return null;
  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Typography variant="h6" component="span">{r.name}</Typography>
      <BolaoStatusChip status={r.status} />
    </Box>
  );
};

const ShowActions = () => {
  const r = useRecordContext();
  return (
    <TopToolbar>
      {r?.status !== 'processed' && <EditButton />}
    </TopToolbar>
  );
};

const ResultActions = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider() as any;
  const chainId = getChainId();

  const [scoreHome, setScoreHome] = useState<string>(
    record?.officialScoreHome != null ? String(record.officialScoreHome) : '',
  );
  const [scoreAway, setScoreAway] = useState<string>(
    record?.officialScoreAway != null ? String(record.officialScoreAway) : '',
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [winners, setWinners] = useState<PreviewWinner[]>([]);
  const [winnersCount, setWinnersCount] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<any>(null);
  const [inactivateOpen, setInactivateOpen] = useState(false);
  const [inactivating, setInactivating] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  if (!record) return null;

  const status: string = record.status;
  const canEditResult = ['active', 'closed', 'result_pending'].includes(status);
  const canPreviewOrApply = status === 'result_pending';
  const isProcessed = status === 'processed';
  // Inativar (off-ramp de visibilidade): qualquer status menos o próprio inactive.
  // Inclui 'processed' — esconde do app um bolão com resultado errado, sem reverter
  // prêmios/cupons já entregues.
  const canInactivate = status !== 'inactive';
  const canReactivate = status === 'inactive';
  // Já processado antes de inativar → reactivate volta para 'processed' (com o
  // resultado), não para 'active'. processedAt nunca é limpo, então é o sinal.
  const wasProcessed = record.processedAt != null;

  const apiCall = async (action: string, payload: any = {}) => {
    const url = `${DEFAULT_API_ADMIN}/chains/${chainId}/boloes/${record.id}/${action}`;
    try {
      const { json } = await httpClientReactAdmin(url, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      // backend wraps responses in createResponse() -> { object, data }
      return json?.data ?? json;
    } catch (err: any) {
      // fetchUtils.fetchJson throws { status, message, body } on non-2xx
      const apiMessage = err?.body?.message || err?.body?.error || err?.message || 'Erro na chamada';
      throw new Error(apiMessage);
    }
  };

  const handleSaveResult = async () => {
    try {
      await apiCall('result', { scoreHome: Number(scoreHome), scoreAway: Number(scoreAway) });
      notify('Resultado cadastrado. Agora você pode avaliar e aplicar recompensas.', { type: 'success' });
      refresh();
    } catch (err: any) {
      notify(err?.message || 'Erro ao cadastrar resultado', { type: 'error' });
    }
  };

  const handleActivate = async () => {
    try {
      await apiCall('activate');
      notify('Bolão ativado!', { type: 'success' });
      refresh();
    } catch (err: any) {
      notify(err?.message || 'Erro ao ativar', { type: 'error' });
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const res = await apiCall('preview-winners');
      setWinners(res?.winners || []);
      setWinnersCount(res?.winnersCount || 0);
    } catch (err: any) {
      notify(err?.message || 'Erro ao avaliar', { type: 'error' });
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const res = await apiCall('process');
      setApplyResult(res);
      notify(`${res?.achievementsCreated || 0} achievements criados.`, { type: 'success' });
      refresh();
    } catch (err: any) {
      notify(err?.message || 'Erro ao aplicar recompensas', { type: 'error' });
    } finally {
      setApplying(false);
    }
  };

  const handleInactivate = async () => {
    setInactivating(true);
    try {
      await apiCall('inactivate');
      notify('Bolão inativado. Ele some do aplicativo e para de dar direito a palpite.', { type: 'success' });
      refresh();
      setInactivateOpen(false);
    } catch (err: any) {
      notify(err?.message || 'Erro ao inativar bolão', { type: 'error' });
    } finally {
      setInactivating(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      await apiCall('reactivate');
      notify('Bolão reativado.', { type: 'success' });
      refresh();
      setReactivateOpen(false);
    } catch (err: any) {
      notify(err?.message || 'Erro ao reativar bolão', { type: 'error' });
    } finally {
      setReactivating(false);
    }
  };

  return (
    <>
      {status === 'draft' && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Ativar bolão</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Antes de ativar, crie <strong>dois brindes</strong> no Clube de Vantagens, ambos vinculados a este bolão:
              <br />
              <br />1️⃣ <strong>Recompensa de Palpite</strong> — o que o cliente ganha por abastecer (vira o direito de palpitar neste bolão).
              <br />2️⃣ <strong>Prêmio por Acerto</strong> — o cupom/desconto que os ganhadores vão receber depois que você processar o resultado.
              <br />
              <br />Ao criar cada brinde no Clube de Vantagens, basta escolher este bolão pelo nome no formulário.
            </Alert>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Button variant="outlined" href="#/gifts/create">Criar brinde no Clube de Vantagens</Button>
            </Stack>
            <Button variant="contained" color="primary" onClick={handleActivate}>Ativar bolão</Button>
          </CardContent>
        </Card>
      )}

      {canEditResult && !isProcessed && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Resultado oficial</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <MuiTextField
                label={record.homeTeamName}
                type="number"
                value={scoreHome}
                onChange={e => setScoreHome(e.target.value)}
                inputProps={{ min: 0, max: 99 }}
                size="small"
              />
              <Typography>×</Typography>
              <MuiTextField
                label={record.awayTeamName}
                type="number"
                value={scoreAway}
                onChange={e => setScoreAway(e.target.value)}
                inputProps={{ min: 0, max: 99 }}
                size="small"
              />
              <Button variant="contained" onClick={handleSaveResult} disabled={scoreHome === '' || scoreAway === ''}>
                Cadastrar resultado
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {canPreviewOrApply && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Recompensas</Typography>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={handlePreview}>Avaliar (preview)</Button>
              <Button variant="contained" color="success" onClick={() => setApplyOpen(true)}>
                Aplicar Recompensas
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {(canInactivate || canReactivate) && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>{canReactivate ? 'Bolão inativo' : 'Inativar bolão'}</Typography>
            {canReactivate ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Este bolão está inativo: não aparece no aplicativo e não dá direito a palpite. Reative para voltar a exibi-lo{wasProcessed ? ' com o resultado.' : ' e aceitar palpites.'}
                </Typography>
                <Button variant="contained" color="primary" onClick={() => setReactivateOpen(true)}>Reativar bolão</Button>
              </>
            ) : isProcessed ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Use para tirar do aplicativo um bolão já processado com resultado errado. O bolão some do app; os prêmios e cupons já entregues aos clientes são mantidos (não há estorno). Você pode reativar depois.
                </Typography>
                <Button variant="outlined" color="error" onClick={() => setInactivateOpen(true)}>Inativar bolão</Button>
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Inativar tira o bolão do aplicativo e para de dar direito a palpite em novos abastecimentos. Os palpites já registrados são mantidos. Você pode reativar depois.
                </Typography>
                <Button variant="outlined" color="error" onClick={() => setInactivateOpen(true)}>Inativar bolão</Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Ganhadores potenciais</DialogTitle>
        <DialogContent>
          {previewLoading ? <CircularProgress /> : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {winnersCount} clientes acertaram o placar exato.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Celular</TableCell>
                    <TableCell>Palpite</TableCell>
                    <TableCell>Submetido em</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {winners.map(w => (
                    <TableRow key={w.guess_id}>
                      <TableCell>{w.name}</TableCell>
                      <TableCell>{w.cellphone}</TableCell>
                      <TableCell>{w.score_home} × {w.score_away}</TableCell>
                      <TableCell>{new Date(w.submitted_at).toLocaleString('pt-BR')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={applyOpen} onClose={() => setApplyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Aplicar Recompensas</DialogTitle>
        <DialogContent>
          {!applyResult ? (
            <Alert severity="warning">
              Esta ação entrega o <strong>Prêmio para Quem Acertar</strong> a todos os clientes que acertaram o placar exato e encerra o bolão. Não pode ser desfeita.
            </Alert>
          ) : (
            <Alert severity="success">
              {applyResult.achievementsCreated} achievements criados, {applyResult.couponsCreated} cupons gerados.
              {applyResult.errors?.length > 0 && (
                <Typography variant="caption" display="block">⚠ {applyResult.errors.length} erros — verificar logs</Typography>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {!applyResult ? (
            <>
              <Button onClick={() => setApplyOpen(false)}>Cancelar</Button>
              <Button variant="contained" color="success" onClick={handleApply} disabled={applying}>
                {applying ? <CircularProgress size={20} /> : 'Confirmar'}
              </Button>
            </>
          ) : (
            <Button onClick={() => { setApplyOpen(false); setApplyResult(null); }}>Fechar</Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={inactivateOpen} onClose={() => setInactivateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Inativar bolão</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            {isProcessed
              ? 'O bolão (e o resultado) deixa de aparecer no aplicativo. Os prêmios e cupons já entregues aos clientes são mantidos — não há estorno. Você pode reativar depois.'
              : 'O bolão deixa de aparecer no aplicativo e para de dar direito a palpite em novos abastecimentos. Palpites já registrados são mantidos. Você pode reativar depois.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInactivateOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleInactivate} disabled={inactivating}>
            {inactivating ? <CircularProgress size={20} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reactivateOpen} onClose={() => setReactivateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reativar bolão</DialogTitle>
        <DialogContent>
          <Alert severity="info">
            {wasProcessed
              ? 'O bolão volta a aparecer no aplicativo com o resultado já processado. Não reabre para palpites e não reprocessa prêmios.'
              : 'O bolão volta a aparecer no aplicativo e a aceitar palpites. Se o prazo para palpitar já tiver passado, ele será fechado automaticamente em instantes.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReactivateOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleReactivate} disabled={reactivating}>
            {reactivating ? <CircularProgress size={20} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const DetailsTabContent = () => {
  const r = useRecordContext();
  if (!r) return null;

  const initials = (name: string) => (name || '?')
    .split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  const placeholderSx = {
    width: 88, height: 88, borderRadius: '50%', bgcolor: '#e5e7eb',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 28, fontWeight: 700, color: '#6b7280',
  };

  const kickoff = r.scheduledAt ? new Date(r.scheduledAt).toLocaleString('pt-BR') : '—';
  const deadline = r.guessDeadlineAt ? new Date(r.guessDeadlineAt).toLocaleString('pt-BR') : '—';
  const officialScore = r.officialScoreHome != null ? `${r.officialScoreHome} × ${r.officialScoreAway}` : null;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Hero — banner + times */}
      <Card sx={{ mb: 2, overflow: 'hidden' }}>
        {r.imageUrl && (
          <Box sx={{ height: 160, width: '100%', backgroundImage: `url(${r.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        )}
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center" justifyContent="center" sx={{ py: 2 }}>
            <Stack alignItems="center" spacing={1} sx={{ minWidth: 120 }}>
              {r.homeTeamLogo
                ? <Box component="img" src={r.homeTeamLogo} alt={r.homeTeamName} sx={{ width: 88, height: 88, objectFit: 'contain' }} />
                : <Box sx={placeholderSx as any}>{initials(r.homeTeamName)}</Box>}
              <Typography variant="subtitle1" fontWeight={600} textAlign="center">{r.homeTeamName}</Typography>
            </Stack>
            {officialScore ? (
              <Box textAlign="center">
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}>Placar oficial</Typography>
                <Typography variant="h3" fontWeight={700} color="primary">{officialScore}</Typography>
              </Box>
            ) : (
              <Typography variant="h3" fontWeight={700} color="text.disabled">×</Typography>
            )}
            <Stack alignItems="center" spacing={1} sx={{ minWidth: 120 }}>
              {r.awayTeamLogo
                ? <Box component="img" src={r.awayTeamLogo} alt={r.awayTeamName} sx={{ width: 88, height: 88, objectFit: 'contain' }} />
                : <Box sx={placeholderSx as any}>{initials(r.awayTeamName)}</Box>}
              <Typography variant="subtitle1" fontWeight={600} textAlign="center">{r.awayTeamName}</Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Linha de cards: identificação + agenda + stats */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Identificação</Typography>
            <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1, mb: 1 }}>
              <Typography variant="h6" fontWeight={600}>{r.name}</Typography>
              <BolaoStatusChip status={r.status} />
            </Box>
            {r.description ? (
              <Typography variant="body2" color="text.secondary">{r.description}</Typography>
            ) : (
              <Typography variant="body2" color="text.disabled">Sem descrição</Typography>
            )}
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Agenda</Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Início do jogo</Typography>
                <Typography variant="body1" fontWeight={500}>{kickoff}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Limite para palpitar</Typography>
                <Typography variant="body1" fontWeight={500}>{deadline}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Estatísticas</Typography>
            <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="h5" fontWeight={700}>{r.stats?.ticketsCount ?? '—'}</Typography>
                <Typography variant="caption" color="text.secondary">Tickets</Typography>
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700}>{r.stats?.submittedCount ?? '—'}</Typography>
                <Typography variant="caption" color="text.secondary">Palpites</Typography>
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} color="success.main">{r.stats?.winnersCount ?? '—'}</Typography>
                <Typography variant="caption" color="text.secondary">Vencedores</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Ações (ativar / cadastrar resultado / avaliar / aplicar) */}
      <ResultActions />
    </Box>
  );
};

const BolaoShow: React.FC = (props: any) => (
  <Show {...props} resource="boloes" title={<Title />} actions={<ShowActions />}>
    <TabbedShowLayout>
      <Tab label="Detalhes">
        <DetailsTabContent />
      </Tab>
      <Tab label="Palpites" path="guesses">
        <BolaoGuessesTab />
      </Tab>
    </TabbedShowLayout>
  </Show>
);

export default BolaoShow;
